// visualizations/dipole_visualizations.js
/**
 * @fileoverview Functions to display dipole moment arrows, E-field vectors,
 * and per-atom force arrows in the canvas.
 *
 * Each dipole is rendered as a two-color arrow encoding polarity:
 *   - Cyan shaft (negative end) → Magenta arrowhead (positive end)
 * The gradient makes the dipole direction instantly readable without
 * a legend, and both colors are absent from CPK atom coloring.
 *
 * E-field at COM replaces the old aggregate pocket dipole (gold arrow).
 * Per-atom force arrows show on E key hold.
 *
 * @typedef {import('BMapsModel').Compound} Compound
 * @typedef {{
 *     compounds: Compound[],
 *     visibleSoluteResidues: AtomGroup[],
 *     electrostaticSources: AtomGroup[],
 * }} DipoleContext
 *
 * @todo - Define DipoleContext class
 *
 * Public interface:
 *     displayDipolesForBindingSite(show, dipoleContexts, params, options)
 *     removeDipolesForBindingSite()
 *     displayPerAtomForces(compound, context)
 *     displayFieldAtCOM(compound, context)
 *     removeForceArrows()
 *     removeFieldArrows()
 */
import {
    findDipoles, defaultDipoleParams,
} from 'BMapsSrc/interactions/dipoles';
import { electrostaticsCache } from 'BMapsSrc/interactions/electrostatics_cache';
import {
    drawCylinder, removeCylinder, drawArrow, removeArrow,
    drawSphere, removeSphere,
} from 'BMapsSrc/MainCanvas';
import {
    Mul3_1, Add3_3, Sub3_3, Length3, Normalize3,
} from 'BMapsSrc/math';
import { AtomGroupTypes } from 'BMapsModel';
import { MolStyles } from 'BMapsSrc/style_manager';
import { hydrogenCheck } from 'BMapsSrc/util/display_utils';
import { isHydrogen } from 'BMapsSrc/util/chem_utils';
import { getFullResidueId } from 'BMapsSrc/util/mol_info_utils';
import { ensureArray, simpleCond } from 'BMapsSrc/util/js_utils';

// Colors: cyan (negative end) → magenta (positive end / arrowhead)
const SHAFT_COLOR = '#00E5FF';
const WEAK_SHAFT_COLOR = 'lime'; // Match weak HBond color
const HEAD_COLOR = '#FF2D9B';

// E-field and force arrow colors
const FIELD_ARROW_COLOR = '#FFB300'; // Amber for E-field at COM
const FORCE_POS_COLOR = '#FF6B6B'; // Red for +charge forces
const FORCE_NEG_COLOR = '#4FC3F7'; // Blue for -charge forces
const FORCE_NEUTRAL_COLOR = '#AAAAAA'; // Grey for near-zero charge

// E-field / force scaling
const FORCE_SCALE = 0.2;
const MAX_ARROW_LEN = 8.0;
const MIN_ARROW_LEN = 0.5;
const MAX_FORCE_ARROWS = 20;

// Dipole unit conversion: 1 e·Å = 4.80320 Debye
const DEBYE_PER_EA = 4.80320;

// Approximate VDW radii by element (spacefill display)
const VDW_RADII = {
    H: 1.2,
    C: 1.7,
    N: 1.55,
    O: 1.52,
    S: 1.8,
    P: 1.8,
    F: 1.47,
    Cl: 1.75,
    Br: 1.85,
    I: 1.98,
    Se: 1.9,
};
// Visual sphere scale factors by display style (fraction of VDW)
const STYLE_RADIUS_SCALE = {
    [MolStyles.spacefill]: 1.5,
    [MolStyles.ballandstick]: 0.35,
};
const DEFAULT_RADIUS_SCALE = 0.35;

/**
 * Get the visual radius of an atom based on display style.
 */
function getAtomSurfaceRadius(atom, compoundStyle) {
    const elem = atom.elem || 'C';
    const vdw = VDW_RADII[elem] || 1.7;
    const scale = STYLE_RADIUS_SCALE[compoundStyle] || DEFAULT_RADIUS_SCALE;
    return vdw * scale;
}

/**
 * Each drawn dipole is tracked as { shaft, center, head } so all
 * shapes can be cleaned up together.
 * @type {Array<{ shaft: *, center: *, head: * }>}
 */
const dipoleShapes = [];

/** E-field at COM shapes */
const fieldShapes = [];

/** Per-atom force arrow shapes */
const forceShapes = [];

/**
 * Display, hide, or redisplay dipole moment arrows in a binding site.
 *
 * @param {boolean} show
 * @param {DipoleContext|DipoleContext[]} dipoleContexts
 * @param {object} params
 * @param {{ includeElectrostatics?: boolean }} [options]
 */
export function displayDipolesForBindingSite(
    show, dipoleContexts,
    params=defaultDipoleParams,
    { includeElectrostatics }={}
) {
    removeDipolesForBindingSite();
    removeFieldArrows();

    if (!show) return;

    // 1. Calculate dipoles for compounds and cofactors, including e-field energies
    // 2. If showing residue dipoles, calculate those, including amide dipoles
    // 3. Display dipoles
    for (const {
        compounds, visibleSoluteResidues, electrostaticSources,
    } of ensureArray(dipoleContexts)) {
        const dipoleGroups = [];
        const dipoleResidues = [];
        compounds.forEach((compound) => dipoleGroups.push(compound));
        for (const res of visibleSoluteResidues) {
            if (res.type === AtomGroupTypes.Cofactor) {
                dipoleGroups.push(res);
            } else if (res.type === AtomGroupTypes.Residue) {
                dipoleResidues.push(res);
            }
        }

        const dipoles = findDipoles(dipoleGroups, params);
        // TODO - pass electrostatic sources into findDipoles and assign energy there
        electrostaticsCache.annotateDipolesWithInteractionEnergy(dipoles, electrostaticSources);

        // Protein residue dipoles: includeElectrostatics state only
        if (includeElectrostatics) {
            const residueDipoles = findDipoles(dipoleResidues, params);
            dipoles.push(...residueDipoles);
        }

        console.log(
            `Dipoles: ${dipoles.length} found from ${compounds.length} compounds${includeElectrostatics ? `, ${dipoleResidues.length} residues` : ''}`
        );
        drawDipoles(dipoles, params);
    }
}

export function removeDipolesForBindingSite() {
    for (const { shaft, center, head } of dipoleShapes) {
        removeCylinder(shaft);
        removeSphere(center);
        removeArrow(head);
    }
    dipoleShapes.length = 0;
}

export function removeFieldArrows() {
    for (const { shaft, head } of fieldShapes) {
        removeCylinder(shaft);
        removeArrow(head);
    }
    fieldShapes.length = 0;
}

export function removeForceArrows() {
    for (const { shaft, head } of forceShapes) {
        removeCylinder(shaft);
        removeArrow(head);
    }
    forceShapes.length = 0;
}

// --- E-field at COM ---

/**
 * Draw E-field vector at the active compound's center of mass.
 * Replaces the old aggregate binding pocket dipole.
 * @param {Compound} compound
 * @param {DipoleContext} context
 *
 * @todo - allow collections of atoms for the efield reference, not just compounds
 */
export function displayFieldAtCOM(compound, context) {
    const { electrostaticSources } = context;
    if (!compound || electrostaticSources.length === 0) return;

    const { field, com } = electrostaticsCache.getFieldAtCOM(electrostaticSources, compound);
    const magnitude = Length3(field);
    console.log(`E-field at COM: |E|=${magnitude.toFixed(3)}, field=[${field.map((v) => v.toFixed(3)).join(', ')}]`);
    if (magnitude < 0.01) return;

    const direction = Normalize3(field);
    // Log scaling: visible at small magnitudes, doesn't explode at large ones.
    // log10(1 + 1.1) ~ 0.32 → halfLen ~ 1.0 + 4*0.32 = 2.3 → total ~4.6 Å
    // log10(1 + 10)  ~ 1.04 → halfLen ~ 1.0 + 4*1.04 = 5.2 → total ~10.3 Å
    const halfLen = Math.min(MAX_HALF_LEN,
        Math.max(MIN_HALF_LEN, MIN_HALF_LEN + 4.0 * Math.log10(1 + magnitude)));

    const halfVec = Mul3_1(direction, halfLen);
    const start = Sub3_3(com, halfVec);
    const end = Add3_3(com, halfVec);

    const radius = 0.18;
    const radiusRatio = 2.0;
    const coneLen = radiusRatio * radius * 3;
    const totalLen = halfLen * 2;
    const splitFrac = Math.max(0.3, 1 - coneLen / totalLen);
    const splitPt = Add3_3(start, Mul3_1(Sub3_3(end, start), splitFrac));

    const tooltip = {
        type: 'table',
        title: 'Electrostatic Field at Ligand Center of Mass',
        body: [
            ['Compound', compound.resSpec],
            ['E-field', `${magnitude.toFixed(1)} kcal/(mol·e·Å)`],
        ],
    };
    const shapeOpts = { description: tooltip };

    const shaft = drawCylinder(
        toObj(start), toObj(splitPt),
        radius, FIELD_ARROW_COLOR,
        false, shapeOpts
    );
    const head = drawArrow(
        toObj(splitPt), toObj(end),
        radius, FIELD_ARROW_COLOR, radiusRatio, shapeOpts
    );

    fieldShapes.push({ shaft, head });
}

// --- Per-atom force arrows ---

/**
 * Display force arrows on ligand atoms from the pocket E-field.
 * Shown when E key is held.
 *
 * @param {Compound} compound
 * @param {DipoleContext} dipoleContext
 * @param {string} [compoundStyle] - MolStyles value for active compound
 * @param {string} [hydrogenPolicy] - HydrogenDisplayOptions value
 *
 * @todo - allow collections of atoms for per-atom forces, not just compounds?
 */
export function displayPerAtomForces(compound, dipoleContext, compoundStyle, hydrogenPolicy) {
    removeForceArrows();
    const { electrostaticSources } = dipoleContext;
    if (!compound || electrostaticSources.length === 0) return;
    const perAtom = electrostaticsCache.getPerAtomFields(electrostaticSources, compound);
    const topN = perAtom.slice(0, MAX_FORCE_ARROWS);

    for (const { atom, force, forceMagnitude } of topN) {
        if (forceMagnitude < 0.1) continue;
        // Skip hydrogens that aren't currently displayed
        if (isHydrogen(atom) && !hydrogenCheck(atom, hydrogenPolicy)) {
            continue;
        }
        const pos = atom.getPosition();
        if (!pos) continue;

        const arrowLen = Math.min(MAX_ARROW_LEN,
            Math.max(MIN_ARROW_LEN, forceMagnitude * FORCE_SCALE));
        const dir = Normalize3(force);
        // Start the arrow just outside the atom's visual surface
        const surfaceR = getAtomSurfaceRadius(atom, compoundStyle);
        const start = Add3_3(pos, Mul3_1(dir, surfaceR));
        const end = Add3_3(start, Mul3_1(dir, arrowLen));

        const chg = Number(atom.charge) || 0;
        const color = simpleCond([
            [chg > 0.05, FORCE_POS_COLOR],
            [chg < -0.05, FORCE_NEG_COLOR],
            [true, FORCE_NEUTRAL_COLOR],
        ]);

        const radius = 0.1;
        const radiusRatio = 2.0;
        const coneLen = radiusRatio * radius * 3;
        const splitFrac = Math.max(0.3, 1 - coneLen / arrowLen);
        const splitPt = Add3_3(start, Mul3_1(dir, arrowLen * splitFrac));

        const tooltip = {
            type: 'table',
            title: 'Electrostatic Force',
            body: [
                ['Atom', atom.atom],
                ['Charge', `${chg.toFixed(3)} e`],
                ['Force', `${forceMagnitude.toFixed(1)} kcal/(mol·Å)`],
            ],
        };
        const shapeOpts = { description: tooltip };

        const shaft = drawCylinder(
            toObj(start), toObj(splitPt),
            radius, color, false, shapeOpts
        );
        const head = drawArrow(
            toObj(splitPt), toObj(end),
            radius, color, radiusRatio, shapeOpts
        );
        forceShapes.push({ shaft, head });
    }
}

// --- internal ---

function drawDipoles(dipoles, params) {
    for (const dipole of dipoles) {
        dipole.isWeak = dipole.magnitude < params.moderateThreshold;
        const shapes = drawOneDipole(dipole, params);
        if (shapes) dipoleShapes.push(shapes);
    }
}

function toObj([x, y, z]) { return { x, y, z }; }

// Arrow half-length scaling: Angstroms per e·Å (unused; scaling is log-based)
const MAGNITUDE_SCALE = 1.0;
const MIN_HALF_LEN = 1.0;
const MAX_HALF_LEN = 10.0;

/**
 * Draw a single dipole as three shapes:
 *   1. Cyan cylinder (shaft) from start to a split point
 *   2. Small sphere at center of mass
 *   3. Magenta arrow (head) from split point to end
 * The split point is chosen so the arrow's cone portion starts
 * where the shaft ends, giving a clean color transition.
 */
function drawOneDipole(dipoleInfo, params) {
    const {
        label, magnitude, direction, centerOfMass,
    } = dipoleInfo;

    // Log scaling: compound dipoles use steeper curve (range 1–8 e·Å),
    // protein residue dipoles use gentler curve to avoid visual clutter
    const logScale = label ? 2.0 : 4.0;
    const halfLen = Math.min(MAX_HALF_LEN,
        Math.max(MIN_HALF_LEN,
            MIN_HALF_LEN + logScale * Math.log10(1 + magnitude)));
    const halfVec = Mul3_1(direction, halfLen);
    const start = Sub3_3(centerOfMass, halfVec);
    const end = Add3_3(centerOfMass, halfVec);

    const radius = 0.15;
    const radiusRatio = 2.0;
    const sphereRadius = radius * 1.5;
    // The arrowhead cone length is roughly 3× the cone radius
    // (radiusRatio * radius). Place the split so the shaft ends
    // where the cone base begins.
    const coneLen = radiusRatio * radius * 3;
    const totalLen = halfLen * 2;
    const splitFrac = Math.max(0.3, 1 - coneLen / totalLen);
    const splitPt = Add3_3(
        start, Mul3_1(Sub3_3(end, start), splitFrac)
    );

    const tooltip = dipoleTooltipInfo(dipoleInfo, params);
    const shapeOpts = { description: tooltip };
    const shaftColor = simpleCond([
        [dipoleInfo.isWeak, WEAK_SHAFT_COLOR],
        [true, SHAFT_COLOR],
    ]);

    const shaft = drawCylinder(
        toObj(start), toObj(splitPt),
        radius, shaftColor,
        false, // not dashed
        shapeOpts
    );

    // Ligand (compound/cofactor) dipoles get a magenta midpoint sphere
    // to distinguish them from protein residue dipoles
    const sphereColor = !label ? HEAD_COLOR : shaftColor;
    const center = drawSphere(
        toObj(centerOfMass), sphereRadius, sphereColor,
        shapeOpts
    );

    const head = drawArrow(
        toObj(splitPt), toObj(end),
        radius, HEAD_COLOR, radiusRatio,
        shapeOpts
    );

    return { shaft, center, head };
}

function dipoleTooltipInfo(dipoleInfo, params) {
    const {
        group, label, magnitude, interactionEnergy,
    } = dipoleInfo;
    const groupLabel = getFullResidueId(group.getAtoms()[0]);
    const strengthLabel = simpleCond([
        [magnitude >= params.strongThreshold, 'Strong'],
        [magnitude >= params.moderateThreshold, 'Moderate'],
        [true, 'Weak'],
    ]);

    const title = label
        ? `Dipole Moment (${label})`
        : 'Dipole Moment';

    const magnitudeStr = `${magnitude.toFixed(2)} e·Å = ${(magnitude * DEBYE_PER_EA).toFixed(2)} D`;

    const body = [
        ['Group', groupLabel],
        ['Magnitude', magnitudeStr],
        ['Strength', strengthLabel],
    ];
    if (interactionEnergy != null) {
        body.push(['Interaction energy', `${interactionEnergy.toFixed(2)} kcal/mol`]);
    }

    return { type: 'table', title, body };
}
